/** @jsxImportSource react */
import { useState } from 'react'
import { Alert, Box, Breadcrumbs, Button, Link, Paper, Stack, Typography } from '@mui/material'
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { recordsAPI } from '../../api/crm'
import { useEntityTypes, useFeatures, usePublishedForm, useTenantKey } from '../../hooks/useCrmQueries'
import DynamicForm from '../../components/records/DynamicForm'
import { allFields, initialValues, toInputValues, toRequestData } from '../../components/records/fieldValues'
import RelatedRecords from '../../components/records/RelatedRecords'
import ActivityTimeline from '../../components/records/ActivityTimeline'
import RecordFiles from '../../components/records/RecordFiles'
import RecordTasks from '../../components/records/RecordTasks'
import RecordMeetings from '../../components/meetings/RecordMeetings'
import RecordProcess from '../../components/process/RecordProcess'
import ApprovalAskDialog, { approvalNeeded, approvalPending } from '../../components/process/ApprovalAskDialog'
import RecordDocuments from '../../components/documents/RecordDocuments'
import EmailDialog from '../../components/records/EmailDialog'
import DuplicateWarning from '../../components/records/DuplicateWarning'
import { useNameOf } from '../../components/records/PersonInput'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import WhatsAppDialog from '../../components/whatsapp/WhatsAppDialog'
import { whatsappAPI } from '../../api/crm'
import { entityLabel, entityLabelPlural, formatDate } from '../../utils/helpers'
import { getErrorList, getErrorMessage, toFieldErrors } from '../../utils/apiErrors'
import { useNotify } from '../../components/common/NotificationProvider'
import Loading from '../../components/common/Loading'
import EmptyState from '../../components/common/EmptyState'
import ConfirmDialog from '../../components/common/ConfirmDialog'
import { SKIP_GUARD, useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import { useEntityAccess } from '../../hooks/usePermissions'

const RecordEditPage = () => {
  const { entityType, recordId } = useParams()
  const [searchParams] = useSearchParams()
  const tenantId = useTenantKey()
  const { forms } = useEntityTypes()

  const recordQuery = useQuery({
    queryKey: [tenantId, 'records', 'one', recordId],
    queryFn: () => recordsAPI.get(recordId),
    enabled: !!recordId,
  })
  const formCode = recordId
    ? recordQuery.data?.formCode
    : searchParams.get('form') || forms.find((f) => f.entityType === entityType)?.code
  const formQuery = usePublishedForm(formCode)

  if (recordQuery.isLoading || formQuery.isLoading) return <Loading />
  if (recordQuery.error) return <Alert severity="error">{getErrorMessage(recordQuery.error)}</Alert>
  if (!formCode || formQuery.error) {
    return <EmptyState title="Form not available" description={formQuery.error ? getErrorMessage(formQuery.error) : 'No published form for this record type.'} />
  }
  if (!formQuery.data) return <Loading />

  return (
    <RecordEditor
      key={`${recordId || 'new'}-${recordQuery.data?.lockVersion ?? ''}`}
      entityType={entityType}
      form={formQuery.data}
      record={recordQuery.data}
    />
  )
}

const RecordEditor = ({ entityType, form, record }) => {
  const navigate = useNavigate()
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const isNew = !record
  const [searchParams] = useSearchParams()
  // "+ New" from a related list pre-fills the link back: ?prefill.<fieldKey>=<recordId>
  const [values, setValues] = useState(() => {
    if (!isNew) return toInputValues(form, record.data)
    const values = initialValues(form)
    allFields(form).forEach((f) => {
      const prefill = searchParams.get(`prefill.${f.fieldKey}`)
      if (prefill) values[f.fieldKey] = prefill
    })
    return values
  })
  const [fieldErrors, setFieldErrors] = useState({})
  const [formError, setFormError] = useState(null)
  const [conflict, setConflict] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [emailing, setEmailing] = useState(false)
  const { emailEnabled } = useFeatures()
  const nameOf = useNameOf()

  const unsavedDialog = useUnsavedChangesGuard(dirty)
  const access = useEntityAccess(entityType)
  const canSave = isNew ? access.canCreate : access.canEdit(record, allFields(form))
  const canDelete = !isNew && access.canDelete(record, allFields(form))

  const formKeys = new Set(allFields(form).map((f) => f.fieldKey))
  const otherData = record ? Object.entries(record.data).filter(([key]) => !formKeys.has(key)) : []

  const [approvalAsk, setApprovalAsk] = useState(null)
  const [whatsapping, setWhatsapping] = useState(false)
  const whatsappAccount = useQuery({ queryKey: [tenantId, 'whatsapp', 'account'], queryFn: whatsappAPI.account, staleTime: 300_000, enabled: !isNew })
  const whatsappOn = !!whatsappAccount.data?.connected && (whatsappAccount.data.contacts || []).some((c) => c.entityType === entityType)
  const saveMutation = useMutation({
    mutationFn: () => {
      const data = toRequestData(form, values)
      return isNew
        ? recordsAPI.create({ formCode: form.code, data })
        : recordsAPI.update(record.id, { data, lockVersion: record.lockVersion })
    },
    onSuccess: (saved) => {
      setDirty(false)
      queryClient.invalidateQueries({ queryKey: [tenantId, 'records'] })
      notify(`${entityLabel(entityType)} ${isNew ? 'created' : 'saved'}`)
      if (isNew) navigate(`/records/${entityType}/${saved.id}`, { replace: true, ...SKIP_GUARD })
      else queryClient.setQueryData([tenantId, 'records', 'one', saved.id], saved)
    },
    onError: (error) => {
      if (approvalNeeded(error) && !isNew) {
        // only what changed goes to the approver
        const data = toRequestData(form, values)
        const changes = Object.fromEntries(Object.entries(data).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(record.data[k])))
        setApprovalAsk({ recordId: record.id, changes, details: error.response.data.details })
        return
      }
      if (approvalPending(error)) {
        setFormError(getErrorMessage(error))
        return
      }
      if (error.response?.status === 409 && !isNew) {
        setConflict(true)
        return
      }
      const errors = toFieldErrors(error)
      setFieldErrors(errors)
      const unmatched = getErrorList(error).filter((e) => !formKeys.has(e.split(':')[0]))
      setFormError(Object.keys(errors).length && !unmatched.length ? 'Please fix the highlighted fields.' : [getErrorMessage(error), ...unmatched].join(' · '))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => recordsAPI.remove(record.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [tenantId, 'records'] })
      notify(`${entityLabel(entityType)} deleted`)
      navigate(`/records/${entityType}`, SKIP_GUARD)
    },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })

  const handleChange = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }))
    setDirty(true)
    if (fieldErrors[key]) setFieldErrors((prev) => ({ ...prev, [key]: undefined }))
  }

  const reload = () => {
    setConflict(false)
    queryClient.invalidateQueries({ queryKey: [tenantId, 'records', 'one', record.id] })
  }

  const title = isNew ? `New ${entityLabel(entityType).toLowerCase()}` : record.title
  // roll-ups change when linked records do (e.g. a line item added below): always show the latest
  const displayValues = isNew ? values : {
    ...values,
    ...Object.fromEntries(allFields(form).filter((f) => f.fieldType === 'ROLLUP').map((f) => [f.fieldKey, record.data[f.fieldKey]])),
  }

  return (
    <Box component="form" onSubmit={(e) => { e.preventDefault(); setFormError(null); saveMutation.mutate() }}>
      <Breadcrumbs sx={{ mb: 1 }}>
        <Link component={RouterLink} to={`/records/${entityType}`} underline="hover" color="inherit">
          {entityLabelPlural(entityType)}
        </Link>
        {(record?.ancestors || []).map((a) => (
          <Link key={a.id} component={RouterLink} to={`/records/${a.entityType}/${a.id}`} underline="hover" color="inherit">{a.title}</Link>
        ))}
        <Typography color="text.primary" noWrap>{title}</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="h4" noWrap>{title}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isNew ? form.name : `Updated ${formatDate(record.updatedOn)} by ${nameOf(record.updatedBy)} · ${form.name} v${record.formVersion}`}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {canDelete && <Button color="error" onClick={() => setConfirmDelete(true)}>Delete</Button>}
          {!isNew && emailEnabled && canSave && (
            <Button startIcon={<MailOutlineIcon />} onClick={() => setEmailing(true)}>Email</Button>
          )}
          {!isNew && whatsappOn && (
            <Button startIcon={<WhatsAppIcon />} onClick={() => setWhatsapping(true)}>WhatsApp</Button>
          )}
          <Button onClick={() => navigate(`/records/${entityType}`)}>{canSave ? 'Cancel' : 'Back'}</Button>
          {canSave && (
            <Button type="submit" variant="contained" disabled={saveMutation.isPending || (!isNew && !dirty)}>
              {saveMutation.isPending ? 'Saving…' : isNew ? 'Create' : 'Save'}
            </Button>
          )}
        </Stack>
      </Box>

      {conflict && (
        <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" onClick={reload}>Reload</Button>}>
          Someone else saved this record while you were editing. Reload to see their changes (your edits will be lost).
        </Alert>
      )}
      {formError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError(null)}>{formError}</Alert>}
      {canSave && <DuplicateWarning entityType={entityType} form={form} values={values} recordId={record?.id} record={record} canMerge={!isNew && !dirty} />}
      {!canSave && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {isNew ? `Your role can't create ${entityLabelPlural(entityType).toLowerCase()}.` : 'View only: your role can’t edit this record.'}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: isNew ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) 380px' }, gap: 3, alignItems: 'start' }}>
        <Box sx={{ minWidth: 0 }}>
          <DynamicForm form={form} values={displayValues} onChange={handleChange} errors={fieldErrors} disabled={saveMutation.isPending || !canSave} lookups={record?.lookups} />

          {otherData.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2.5, mt: 2 }}>
              <Typography variant="subtitle2">Fields no longer on the form</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Kept for reference; they can’t be edited.</Typography>
              {otherData.map(([key, value]) => (
                <Typography key={key} variant="body2"><strong>{key}:</strong> {Array.isArray(value) ? value.join(', ') : String(value)}</Typography>
              ))}
            </Paper>
          )}

          {!isNew && <RecordFiles record={record} canUpload={canSave} />}
          {!isNew && <RecordDocuments record={record} canEdit={canSave} />}
          {!isNew && <RelatedRecords record={record} />}
        </Box>
        {!isNew && (
          <Box sx={{ minWidth: 0 }}>
            <RecordProcess record={record} canEdit={canSave} />
            <RecordTasks record={record} />
            <RecordMeetings record={record} />
            <ActivityTimeline record={record} form={form} />
          </Box>
        )}
      </Box>

      {unsavedDialog}
      {emailing && <EmailDialog open record={record} form={form} onClose={() => setEmailing(false)} />}
      {whatsapping && <WhatsAppDialog record={record} onClose={() => setWhatsapping(false)} />}
      {approvalAsk && (
        <ApprovalAskDialog ask={approvalAsk} onClose={() => setApprovalAsk(null)}
          onAsked={() => { setDirty(false); queryClient.invalidateQueries({ queryKey: [tenantId, 'records'] }) }} />
      )}
      {!isNew && (
        <ConfirmDialog
          open={confirmDelete}
          title={`Delete ${entityLabel(entityType).toLowerCase()}?`}
          message={`"${title}" will be removed from all lists.`}
          confirmLabel="Delete"
          destructive
          busy={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </Box>
  )
}

export default RecordEditPage
